import { cn } from '../../../lib/utils'
import { Select } from '../../ui/Select'
import type { TableState } from './types'

const PAGE_SIZES = [5, 10, 25, 50]

/* A page whose initial size is not one of the presets — Versions opens at 8 —
 * would otherwise bind the <Select> to a value with no matching option and
 * render an empty trigger. Fold the current size in rather than making every
 * caller pick from the list. */
function pageSizeOptions(current: number) {
  const sizes = PAGE_SIZES.includes(current)
    ? PAGE_SIZES
    : [...PAGE_SIZES, current].sort((a, b) => a - b)
  return sizes.map((size) => ({ value: String(size), label: String(size) }))
}

interface DataTablePaginationProps {
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  total: number
  className?: string
}

/** Always first, last and a window of three: [1, …, 7, 8, 9, …, 24]. */
function pageList(current: number, last: number): (number | 'gap')[] {
  if (last <= 7) return Array.from({ length: last }, (_, index) => index + 1)

  const wanted = [1, last, current, current - 1, current + 1]
    .filter((page) => page >= 1 && page <= last)
    .sort((a, b) => a - b)

  const result: (number | 'gap')[] = []
  let previous = 0
  for (const page of [...new Set(wanted)]) {
    if (previous && page - previous > 1) result.push('gap')
    result.push(page)
    previous = page
  }
  return result
}

const arrowClass =
  'grid size-8 place-items-center rounded-md border border-border text-fg-muted transition-colors outline-none hover:bg-bg-subtle disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent'

/**
 * Shared footer for both <DataTable> and <DataGrid> so the two can never drift
 * into looking like different products.
 */
export function DataTablePagination({
  state,
  onStateChange,
  total,
  className,
}: DataTablePaginationProps) {
  const lastPage = Math.max(1, Math.ceil(total / state.pageSize))
  const first = total === 0 ? 0 : (state.page - 1) * state.pageSize + 1
  const last = Math.min(state.page * state.pageSize, total)

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="text-fg-muted flex items-center gap-2.5 text-sm">
        <span>Rows per page</span>
        <Select
          aria-label="Rows per page"
          options={pageSizeOptions(state.pageSize)}
          value={String(state.pageSize)}
          onValueChange={(value) => onStateChange({ pageSize: Number(value), page: 1 })}
          triggerClassName="h-8 w-18"
        />
        <span className="tabular-nums">
          {first}–{last} of {total}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={state.page <= 1}
          onClick={() => onStateChange({ page: state.page - 1 })}
          className={arrowClass}
        >
          ‹
        </button>

        {pageList(state.page, lastPage).map((entry, index) =>
          entry === 'gap' ? (
            <span key={`gap-${index}`} className="text-fg-subtle px-1 text-sm">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              aria-current={entry === state.page ? 'page' : undefined}
              onClick={() => onStateChange({ page: entry })}
              className={cn(
                'focus-visible:outline-accent h-8 min-w-8 rounded-md border px-1.5 text-sm tabular-nums transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-1',
                entry === state.page
                  ? 'border-fg bg-fg text-bg'
                  : 'border-border text-fg-muted hover:border-border-strong',
              )}
            >
              {entry}
            </button>
          ),
        )}

        <button
          type="button"
          aria-label="Next page"
          disabled={state.page >= lastPage}
          onClick={() => onStateChange({ page: state.page + 1 })}
          className={arrowClass}
        >
          ›
        </button>
      </div>
    </div>
  )
}

export type { DataTablePaginationProps }
